import { createListenerMiddleware, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { saveStorageValue } from 'hooks/use-storage/use-storage.utils';
import { RootState } from 'store/root.types';
import { Sdk } from 'utils/iq/sdk/sdk';
import { getStorageValuesWithPrefix } from 'utils/storage';

export type ChatSettingsOptionsType = {
  explainQueries: boolean;
  unnestHelper: boolean;
  useInsertHelper: boolean;
  sdkLanguage: Sdk['value'];
  support_7_6: boolean;
};

export type ChatSettingsValue = {
  options: ChatSettingsOptionsType;
};

export interface ChatSettingsState {
  value: ChatSettingsValue;
}

export const chatSettingsStorageKey = 'iq:chat-settings';

export const chatSettingsSliceName = 'chatSettings';

const getInitialState = () => {
  const chatSettingsOptions = getStorageValuesWithPrefix(chatSettingsStorageKey);
  return chatSettingsOptions.reduce<ChatSettingsOptionsType>(
    (initialState, { value }) => {
      initialState.explainQueries = value?.explainQueries ?? initialState.explainQueries;
      initialState.unnestHelper = value?.unnestHelper ?? initialState.unnestHelper;
      initialState.useInsertHelper = value?.useInsertHelper ?? initialState.useInsertHelper;
      initialState.sdkLanguage = value?.sdkLanguage ?? initialState.sdkLanguage;
      initialState.support_7_6 = value?.support_7_6 ?? initialState.support_7_6;
      return initialState;
    },
    {
      explainQueries: false,
      unnestHelper: true,
      useInsertHelper: true,
      sdkLanguage: '' as Sdk['value'],
      support_7_6: true,
    }
  );
};

const initialState: ChatSettingsState = {
  value: {
    options: getInitialState(),
  },
};

export const chatSettingsSlice = createSlice({
  name: chatSettingsSliceName,
  initialState,
  reducers: {
    loadSettings: (state, action: PayloadAction<ChatSettingsValue>) => {
      state.value = action.payload;
    },
    updateOptions: (state, action: PayloadAction<Partial<ChatSettingsOptionsType>>) => {
      state.value.options = { ...state.value.options, ...action.payload };
    },
  },
});

export const chatSettingsMiddleware = createListenerMiddleware();

export const { loadSettings, updateOptions } = chatSettingsSlice.actions;

chatSettingsMiddleware.startListening({
  matcher: isAnyOf(loadSettings, updateOptions),
  effect: (action, listenerApi) => {
    const chatSettingsState = (listenerApi.getState() as RootState)[chatSettingsSliceName];

    saveStorageValue({
      storageKey: chatSettingsStorageKey,
      value: chatSettingsState.value.options,
    });
  },
});

export default chatSettingsSlice.reducer;
